import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardSidebar from '../components/DashboardSidebar';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { 
  FileText, Clock, CheckCircle, AlertCircle, ArrowLeft, XCircle,
  Download, Eye, User, Calendar, Globe, CreditCard, X, Shield, ShieldCheck, ShieldX,
  Upload, MessageSquare, Send, RefreshCw
} from 'lucide-react';
import { applicationsApi, documentsApi } from '../lib/api';
import CountryFlag from '../components/CountryFlag';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ApplicationDetail = ({ isAdmin = false, isEmployee = false }) => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [verifyingDoc, setVerifyingDoc] = useState(null);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [reuploadingDoc, setReuploadingDoc] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);

  useEffect(() => {
    fetchApplication();
    fetchActivityLogs();
  }, [applicationId]);

  const fetchApplication = async () => {
    try {
      const response = await applicationsApi.getById(applicationId);
      setApplication(response.data);
    } catch (error) {
      console.error('Error fetching application:', error);
      toast.error('Failed to load application');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLogs = async () => {
    setActivityLoading(true);
    try {
      const res = await applicationsApi.getActivityLog(applicationId);
      setActivityLogs(res.data.activity_logs || []);
    } catch (e) {
      console.error('Error fetching activity log:', e);
    } finally {
      setActivityLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setCommentLoading(true);
    try {
      await applicationsApi.addComment(applicationId, newComment.trim());
      setNewComment('');
      toast.success('Comment added');
      fetchActivityLogs();
    } catch (e) {
      toast.error('Failed to add comment');
    } finally {
      setCommentLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-700 border-red-200';
      case 'submitted':
      case 'under_review':
      case 'processing':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-6 h-6 text-green-600" />;
      case 'rejected': return <AlertCircle className="w-6 h-6 text-red-600" />;
      case 'submitted':
      case 'under_review':
      case 'processing':
        return <Clock className="w-6 h-6 text-yellow-600" />;
      default: return <FileText className="w-6 h-6 text-gray-600" />;
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      await applicationsApi.update(applicationId, { status: newStatus });
      setApplication({ ...application, status: newStatus });
      toast.success(`Application status updated to ${newStatus}`);
      fetchActivityLogs();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleViewDocument = (doc) => {
    const token = localStorage.getItem('access_token');
    const url = `${API_URL}/api/documents/${doc.document_id}/download`;
    window.open(`${url}?token=${token}`, '_blank');
  };

  const handleVerifyDocument = async (docId, isVerified) => {
    setVerifyLoading(true);
    try {
      await documentsApi.verify(docId, isVerified, verificationNotes);
      toast.success(isVerified ? 'Document verified' : 'Document rejected');
      setVerifyingDoc(null);
      setVerificationNotes('');
      await fetchApplication();
      fetchActivityLogs();
    } catch (error) {
      console.error('Error verifying document:', error);
      toast.error('Failed to update verification');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleReupload = async (file, doc) => {
    setReuploadingDoc(doc.document_id);
    try {
      await documentsApi.reupload(doc.document_id, file);
      toast.success('Document re-uploaded! Admin will be notified for review.');
      await fetchApplication();
      fetchActivityLogs();
    } catch (error) {
      console.error('Error re-uploading:', error);
      toast.error('Failed to re-upload document');
    } finally {
      setReuploadingDoc(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F0E6]">
        <DashboardSidebar />
        <main className="dashboard-content p-6 lg:p-8 pt-20 lg:pt-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
            <div className="bg-white rounded-xl p-6 space-y-4">
              <div className="h-6 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen bg-[#F5F0E6]">
        <DashboardSidebar />
        <main className="dashboard-content p-6 lg:p-8 pt-20 lg:pt-8">
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-[#D1CBBF] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#0A1E40] mb-2">Application not found</h3>
            <Button onClick={() => navigate(-1)}>Go Back</Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <DashboardSidebar />
      
      <main className="dashboard-content p-6 lg:p-8 pt-20 lg:pt-8" data-testid="application-detail-page">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <span className="text-3xl"><CountryFlag code={application.country?.code} size="xl" /></span>
                <div>
                  <h1 className="text-2xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {application.country?.name} Visa Application
                  </h1>
                  <p className="text-[#4A5568]">{application.application_id}</p>
                </div>
              </div>
            </div>
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border ${getStatusColor(application.status)}`}>
              {getStatusIcon(application.status)}
              <span className="font-medium capitalize">{application.status?.replace('_', ' ')}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Rejected Documents Alert for Applicants */}
          {!isAdmin && !isEmployee && application.documents?.some(d => d.verification_notes && !d.is_verified) && (
            <div className="lg:col-span-3 flex items-center gap-4 p-5 bg-red-50 border-2 border-red-200 rounded-xl" data-testid="rejected-docs-alert">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <ShieldX className="w-6 h-6 text-red-500" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-red-700">
                  {application.documents.filter(d => d.verification_notes && !d.is_verified).length} document{application.documents.filter(d => d.verification_notes && !d.is_verified).length > 1 ? 's' : ''} rejected by reviewer
                </p>
                <p className="text-sm text-red-600 mt-0.5">
                  Please scroll down to the Documents section and re-upload the rejected documents to continue processing.
                </p>
              </div>
              <a href="#documents-section" className="shrink-0">
                <Button className="bg-red-600 hover:bg-red-700 text-white" data-testid="scroll-to-docs-btn">
                  <Upload className="w-4 h-4 mr-2" /> Re-upload Now
                </Button>
              </a>
            </div>
          )}

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Trip Details */}
            <div className="bg-white rounded-xl p-6 border border-[#D1CBBF]">
              <h2 className="text-lg font-semibold text-[#0A1E40] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Trip Details
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-[#FF5A36]" />
                  <div>
                    <p className="text-sm text-[#4A5568]">Destination</p>
                    <p className="font-medium">{application.country?.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-[#FF5A36]" />
                  <div>
                    <p className="text-sm text-[#4A5568]">Visa Type</p>
                    <p className="font-medium">{application.visa_type?.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-[#FF5A36]" />
                  <div>
                    <p className="text-sm text-[#4A5568]">Travel Date</p>
                    <p className="font-medium">{application.travel_date ? new Date(application.travel_date).toLocaleDateString() : '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-[#FF5A36]" />
                  <div>
                    <p className="text-sm text-[#4A5568]">Return Date</p>
                    <p className="font-medium">{application.return_date ? new Date(application.return_date).toLocaleDateString() : '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-[#FF5A36]" />
                  <div>
                    <p className="text-sm text-[#4A5568]">Travelers</p>
                    <p className="font-medium">{application.num_travelers}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <CreditCard className="w-5 h-5 text-[#FF5A36]" />
                  <div>
                    <p className="text-sm text-[#4A5568]">Total Fee</p>
                    <p className="font-medium">{application.total_fee?.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Travelers */}
            <div className="bg-white rounded-xl p-6 border border-[#D1CBBF]">
              <h2 className="text-lg font-semibold text-[#0A1E40] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Travelers ({application.travelers?.length || 0})
              </h2>
              <div className="space-y-4">
                {application.travelers?.map((traveler, index) => (
                  <div key={index} className="p-4 bg-[#F5F0E6] rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium text-[#0A1E40]">
                        Traveler {index + 1}: {traveler.first_name} {traveler.last_name}
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-[#4A5568]">Passport No.</p>
                        <p className="font-medium">{traveler.passport_number}</p>
                      </div>
                      <div>
                        <p className="text-[#4A5568]">DOB</p>
                        <p className="font-medium">{traveler.dob}</p>
                      </div>
                      <div>
                        <p className="text-[#4A5568]">Nationality</p>
                        <p className="font-medium">{traveler.nationality}</p>
                      </div>
                      <div>
                        <p className="text-[#4A5568]">Gender</p>
                        <p className="font-medium">{traveler.gender}</p>
                      </div>
                      <div>
                        <p className="text-[#4A5568]">Passport Expiry</p>
                        <p className="font-medium">{traveler.passport_expiry}</p>
                      </div>
                      <div>
                        <p className="text-[#4A5568]">Occupation</p>
                        <p className="font-medium">{traveler.occupation || '-'}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {(!application.travelers || application.travelers.length === 0) && (
                  <p className="text-[#4A5568] text-center py-4">No traveler information available</p>
                )}
              </div>
            </div>

            {/* Documents */}
            <div className="bg-white rounded-xl p-6 border border-[#D1CBBF]" data-testid="documents-section">
              <h2 className="text-lg font-semibold text-[#0A1E40] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Documents ({application.documents?.length || 0})
              </h2>
              {application.documents?.length > 0 ? (
                <div className="space-y-3">
                  {application.documents.map((doc, index) => (
                    <div key={index} className="p-4 bg-[#F5F0E6] rounded-lg" data-testid={`document-row-${index}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <FileText className="w-5 h-5 text-[#0A1E40] shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium truncate">{doc.filename}</p>
                            <p className="text-xs text-[#4A5568]">
                              <span className="capitalize">{doc.document_type?.replace('_', ' ')}</span>
                              {doc.traveler_index !== undefined && ` - Traveler ${doc.traveler_index + 1}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-3">
                          {/* Verification badge */}
                          {doc.is_verified === true && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium" data-testid={`doc-verified-${index}`}>
                              <ShieldCheck className="w-3.5 h-3.5" /> Verified
                            </span>
                          )}
                          {doc.is_verified === false && verifyingDoc !== doc.document_id && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium" data-testid={`doc-pending-${index}`}>
                              <Shield className="w-3.5 h-3.5" /> {doc.verification_notes ? 'Rejected' : 'Pending'}
                            </span>
                          )}
                          
                          {/* View button */}
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleViewDocument(doc)}
                            title="View document"
                            data-testid={`view-doc-btn-${index}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          
                          {/* Verification buttons (Admin/Employee only) */}
                          {(isAdmin || isEmployee) && !doc.is_verified && (
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-green-600 hover:bg-green-50 hover:text-green-700"
                                onClick={() => handleVerifyDocument(doc.document_id, true)}
                                title="Approve document"
                                data-testid={`approve-doc-btn-${index}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:bg-red-50 hover:text-red-700"
                                onClick={() => {
                                  setVerifyingDoc(doc.document_id);
                                  setVerificationNotes('');
                                }}
                                title="Reject document"
                                data-testid={`reject-doc-btn-${index}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Rejection notes form */}
                      {verifyingDoc === doc.document_id && (
                        <div className="mt-3 pt-3 border-t border-[#D1CBBF]" data-testid={`reject-form-${index}`}>
                          <Textarea
                            placeholder="Add rejection notes (optional)..."
                            value={verificationNotes}
                            onChange={(e) => setVerificationNotes(e.target.value)}
                            rows={2}
                            className="mb-2"
                          />
                          <div className="flex gap-2 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setVerifyingDoc(null)}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              className="bg-red-600 hover:bg-red-700 text-white"
                              onClick={() => handleVerifyDocument(doc.document_id, false)}
                              disabled={verifyLoading}
                              data-testid={`confirm-reject-btn-${index}`}
                            >
                              {verifyLoading ? 'Rejecting...' : 'Reject Document'}
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* Show existing rejection notes */}
                      {doc.verification_notes && !doc.is_verified && verifyingDoc !== doc.document_id && (
                        <div className="mt-3 pt-3 border-t border-[#D1CBBF]">
                          <p className="text-xs font-medium text-red-600 mb-1">Rejection Note:</p>
                          <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-lg">{doc.verification_notes}</p>
                          
                          {/* Re-upload button for applicants */}
                          {!isAdmin && !isEmployee && (
                            <div className="mt-3">
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                className="hidden"
                                id={`reupload-${doc.document_id}`}
                                onChange={(e) => e.target.files?.[0] && handleReupload(e.target.files[0], doc)}
                              />
                              <label htmlFor={`reupload-${doc.document_id}`} className="cursor-pointer">
                                <span
                                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                                    reuploadingDoc === doc.document_id
                                      ? 'bg-gray-200 text-gray-500 cursor-wait'
                                      : 'bg-[#FF5A36] text-white hover:bg-[#E0492B]'
                                  }`}
                                  data-testid={`reupload-btn-${index}`}
                                >
                                  <Upload className="w-4 h-4" />
                                  {reuploadingDoc === doc.document_id ? 'Uploading...' : 'Re-upload Document'}
                                </span>
                              </label>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#4A5568] text-center py-4">No documents uploaded</p>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status Actions (Admin/Employee only) */}
            {(isAdmin || isEmployee) && (
              <div className="bg-white rounded-xl p-6 border border-[#D1CBBF]">
                <h2 className="text-lg font-semibold text-[#0A1E40] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Update Status
                </h2>
                <div className="space-y-2">
                  {['under_review', 'processing', 'approved', 'rejected'].map((status) => (
                    <Button
                      key={status}
                      variant={application.status === status ? 'default' : 'outline'}
                      className="w-full justify-start capitalize"
                      onClick={() => handleStatusUpdate(status)}
                      disabled={application.status === status}
                      data-testid={`status-btn-${status}`}
                    >
                      {status.replace('_', ' ')}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Document Summary (Admin/Employee only) */}
            {(isAdmin || isEmployee) && application.documents?.length > 0 && (
              <div className="bg-white rounded-xl p-6 border border-[#D1CBBF]">
                <h2 className="text-lg font-semibold text-[#0A1E40] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Verification Summary
                </h2>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium text-green-700">Verified</span>
                    </div>
                    <span className="text-lg font-bold text-green-700" data-testid="verified-count">
                      {application.documents.filter(d => d.is_verified === true).length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-amber-600" />
                      <span className="text-sm font-medium text-amber-700">Pending</span>
                    </div>
                    <span className="text-lg font-bold text-amber-700" data-testid="pending-count">
                      {application.documents.filter(d => d.is_verified !== true).length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-[#F5F0E6] rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-[#0A1E40]" />
                      <span className="text-sm font-medium text-[#0A1E40]">Total</span>
                    </div>
                    <span className="text-lg font-bold text-[#0A1E40]" data-testid="total-doc-count">
                      {application.documents.length}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Communication Track / Activity Log */}
            <div className="bg-white rounded-xl p-6 border border-[#D1CBBF]" data-testid="activity-log-section">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-semibold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Communication Track
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchActivityLogs}
                  className="text-[#4A5568] hover:text-[#0A1E40]"
                  data-testid="refresh-activity-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>

              {/* Add Comment Box */}
              <div className="mb-6 border border-[#D1CBBF] rounded-lg p-3" data-testid="comment-box">
                <Textarea
                  placeholder="Add a comment or note about this application..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="border-0 p-0 focus-visible:ring-0 min-h-[60px] text-sm resize-none"
                  data-testid="comment-input"
                />
                <div className="flex justify-end mt-2">
                  <Button
                    size="sm"
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || commentLoading}
                    className="bg-[#0A1E40] hover:bg-[#0A1E40]/90 text-white text-xs"
                    data-testid="send-comment-btn"
                  >
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                    {commentLoading ? 'Sending...' : 'Add Comment'}
                  </Button>
                </div>
              </div>

              {/* Activity Timeline */}
              {activityLoading ? (
                <div className="space-y-3">
                  {[1,2,3].map(i => (
                    <div key={i} className="animate-pulse flex gap-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-full shrink-0" />
                      <div className="flex-1 space-y-1">
                        <div className="h-3 bg-gray-200 rounded w-2/3" />
                        <div className="h-3 bg-gray-200 rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : activityLogs.length === 0 && (!application?.timeline || application.timeline.length === 0) ? (
                <div className="text-center py-8 text-[#4A5568]">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 text-[#D1CBBF]" />
                  <p className="text-sm">No activity yet. Events will appear here as the application progresses.</p>
                </div>
              ) : (
                <div className="space-y-0">
                  {/* Merge old timeline + new activity logs, sort by date desc */}
                  {[
                    ...activityLogs.map(log => ({
                      ...log,
                      _source: 'log',
                      _date: log.created_at
                    })),
                    ...(application?.timeline || [])
                      .filter(t => !activityLogs.some(l => l.event_type === 'status_change' && l.details?.new_status === t.status))
                      .map(t => ({
                        event_type: 'status_change',
                        message: t.note || `Status: ${t.status?.replace('_', ' ')}`,
                        user_name: 'System',
                        user_role: 'system',
                        created_at: t.timestamp,
                        details: { new_status: t.status },
                        _source: 'timeline',
                        _date: t.timestamp
                      }))
                  ]
                  .sort((a, b) => new Date(b._date) - new Date(a._date))
                  .map((event, index, arr) => {
                    const getEventStyle = (type) => {
                      switch (type) {
                        case 'status_change': return { icon: <Clock className="w-4 h-4" />, bg: 'bg-blue-100', text: 'text-blue-600', label: 'Status Update' };
                        case 'document_verified': return { icon: <ShieldCheck className="w-4 h-4" />, bg: 'bg-green-100', text: 'text-green-600', label: 'Document Verified' };
                        case 'document_rejected': return { icon: <ShieldX className="w-4 h-4" />, bg: 'bg-red-100', text: 'text-red-600', label: 'Document Rejected' };
                        case 'document_reuploaded': return { icon: <Upload className="w-4 h-4" />, bg: 'bg-orange-100', text: 'text-orange-600', label: 'Document Re-uploaded' };
                        case 'application_submitted': return { icon: <CheckCircle className="w-4 h-4" />, bg: 'bg-emerald-100', text: 'text-emerald-600', label: 'Submitted' };
                        case 'comment': return { icon: <MessageSquare className="w-4 h-4" />, bg: 'bg-purple-100', text: 'text-purple-600', label: 'Comment' };
                        default: return { icon: <FileText className="w-4 h-4" />, bg: 'bg-gray-100', text: 'text-gray-600', label: 'Event' };
                      }
                    };
                    const style = getEventStyle(event.event_type);
                    
                    return (
                      <div key={event.log_id || `tl-${index}`} className="flex gap-3 relative" data-testid={`activity-item-${index}`}>
                        {/* Vertical line */}
                        <div className="flex flex-col items-center shrink-0">
                          <div className={`w-8 h-8 rounded-full ${style.bg} flex items-center justify-center ${style.text}`}>
                            {style.icon}
                          </div>
                          {index < arr.length - 1 && (
                            <div className="w-0.5 flex-1 bg-[#D1CBBF]/50 my-1" />
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className="pb-5 flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${style.bg} ${style.text}`}>
                              {style.label}
                            </span>
                            <span className="text-xs text-[#9CA3AF]">
                              {new Date(event.created_at || event._date).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <p className="text-sm text-[#0A1E40] mt-1 font-medium">
                            {event.message}
                          </p>
                          {event.user_name && event.user_name !== 'System' && (
                            <p className="text-xs text-[#4A5568] mt-0.5 flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {event.user_name}
                              {event.user_role && <span className="text-[#9CA3AF]">({event.user_role})</span>}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Payment Status */}
            <div className="bg-white rounded-xl p-6 border border-[#D1CBBF]">
              <h2 className="text-lg font-semibold text-[#0A1E40] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Payment
              </h2>
              <div className={`flex items-center gap-3 p-3 rounded-lg ${
                application.payment_status === 'completed' 
                  ? 'bg-green-50' 
                  : 'bg-yellow-50'
              }`}>
                {application.payment_status === 'completed' ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <Clock className="w-5 h-5 text-yellow-600" />
                )}
                <div>
                  <p className="font-medium capitalize">{application.payment_status}</p>
                  <p className="text-sm">{application.total_fee?.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ApplicationDetail;
